import { rbacTranslationRef } from '@backstage-community/plugin-rbac/alpha';
import { coreComponentsTranslationRef } from '@backstage/core-components';
import {
  createTranslationRef,
  createTranslationResource,
} from '@backstage/core-plugin-api/alpha';
import { apiDocsTranslationRef } from '@backstage/plugin-api-docs';
import { catalogTranslationRef } from '@backstage/plugin-catalog';
import { catalogGraphTranslationRef } from '@backstage/plugin-catalog-graph';
import { catalogImportTranslationRef } from '@backstage/plugin-catalog-import';
import { catalogReactTranslationRef } from '@backstage/plugin-catalog-react';
import { kubernetesTranslationRef } from '@backstage/plugin-kubernetes';
import { kubernetesClusterTranslationRef } from '@backstage/plugin-kubernetes-cluster';
import { kubernetesReactTranslationRef } from '@backstage/plugin-kubernetes-react';
import { notificationsTranslationRef } from '@backstage/plugin-notifications';
import { orgTranslationRef } from '@backstage/plugin-org';
import { scaffolderTranslationRef } from '@backstage/plugin-scaffolder';
import { scaffolderReactTranslationRef } from '@backstage/plugin-scaffolder-react';
import { searchTranslationRef } from '@backstage/plugin-search';
import { searchReactTranslationRef } from '@backstage/plugin-search-react';
import { userSettingsTranslationRef } from '@backstage/plugin-user-settings';
import { accessTranslationRef } from 'adp-access-react';
import { doraMetricsTranslationRef } from '@c2l2c/backstage-plugin-dora-metrics';
import { argoCdTranslationRef } from 'plugin-argo-cd';
import { gitflicPipelinesTranslationRef } from 'plugin-gitflic-pipelines';
import { techdocsTranslationRef } from './translations/techdocs.ru';

export const appTranslationRef = createTranslationRef({
  id: 'adp-app',
  messages: {
    root: {
      ariaLabelHome: 'Home',
      search: 'Search',
      menu: 'Menu',
      catalog: 'Catalog',
      myGroup: 'My Group',
      myGroups: 'My Groups',
      apis: 'APIs',
      docs: 'Docs',
      create: 'Create...',
      settings: 'Settings',
      notifications: 'Notifications',
      access: 'Access',
      catalogGraph: 'Catalog Graph',
      roleManagement: 'ADP Permissions',
      argoCd: 'Argo CD',
      visualizer: 'Visualizer',
      registerExistingComponent: 'Register Existing Component',
    },
    signIn: {
      provider: {
        title: 'bootsman',
        message: 'Sign in using bootsman SSO',
      },
      errors: {
        popupClosed: 'Sign-in window was closed. Try signing in again.',
      },
    },
    searchPage: {
      title: 'Search',
      resultType: 'Result Type',
      softwareCatalog: 'Software Catalog',
      documentation: 'Documentation',
      entity: 'Entity',
      kind: 'Kind',
      lifecycle: 'Lifecycle',
      kindOption: {
        component: 'Component',
        template: 'Template',
      },
    },
    techDocs: {
      versionSelector: {
        label: 'Version',
      },
    },
    entityPage: {
      tabs: {
        overview: 'Overview',
        cicd: 'CI/CD',
        kubernetes: 'Kubernetes',
        api: 'API',
        dependencies: 'Dependencies',
        bindings: 'Access permissions',
        docs: 'Docs',
        definition: 'Definition',
        diagram: 'Diagram',
      },
      systemDiagram: {
        title: 'System Diagram',
      },
      cicdEmptyState: {
        title: 'No CI/CD available for this entity',
        description:
          'You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below.',
        readMore: 'Read more',
      },
      bindingsAccessWarning: {
        title: 'You do not have access to view access permissions.',
        description:
          'Contact an administrator to request the required permissions.',
      },
      contextMenu: {
        deleteComponent: 'Delete component',
        deleteComponentTooltip:
          'Delete Argo CD applications, Bootsman projects, catalog entity, and Gitflic repository',
        changeComponentOwner: 'Change owner',
        changeComponentOwnerTooltip:
          'Select a component and a new user or group owner',
      },
    },
  },
  translations: {
    ru: () => import('./translations/ru'),
  },
});

const apiDocsTranslations = createTranslationResource({
  ref: apiDocsTranslationRef,
  translations: {
    ru: () => import('./translations/api-docs.ru'),
  },
});

const userSettingsTranslations = createTranslationResource({
  ref: userSettingsTranslationRef,
  translations: {
    ru: () => import('./translations/user-settings.ru'),
  },
});

const catalogTranslations = createTranslationResource({
  ref: catalogTranslationRef,
  translations: {
    ru: () => import('./translations/catalog.ru'),
  },
});

const catalogImportTranslations = createTranslationResource({
  ref: catalogImportTranslationRef,
  translations: {
    ru: () => import('./translations/catalog-import.ru'),
  },
});

const searchTranslations = createTranslationResource({
  ref: searchTranslationRef,
  translations: {
    ru: () => import('./translations/search.ru'),
  },
});

const searchReactTranslations = createTranslationResource({
  ref: searchReactTranslationRef,
  translations: {
    ru: () => import('./translations/search-react.ru'),
  },
});

const kubernetesTranslations = createTranslationResource({
  ref: kubernetesTranslationRef,
  translations: {
    ru: () => import('./translations/kubernetes.ru'),
  },
});

const kubernetesReactTranslations = createTranslationResource({
  ref: kubernetesReactTranslationRef,
  translations: {
    ru: () => import('./translations/kubernetes-react.ru'),
  },
});

const kubernetesClusterTranslations = createTranslationResource({
  ref: kubernetesClusterTranslationRef,
  translations: {
    ru: () => import('./translations/kubernetes-cluster.ru'),
  },
});

const techdocsTranslations = createTranslationResource({
  ref: techdocsTranslationRef,
  translations: {
    ru: () => import('./translations/techdocs.ru'),
  },
});

const coreComponentsTranslations = createTranslationResource({
  ref: coreComponentsTranslationRef,
  translations: {
    ru: () => import('./translations/core-components.ru'),
  },
});

const catalogGraphTranslations = createTranslationResource({
  ref: catalogGraphTranslationRef,
  translations: {
    ru: () => import('./translations/catalog-graph.ru'),
  },
});

const catalogReactTranslations = createTranslationResource({
  ref: catalogReactTranslationRef,
  translations: {
    ru: () => import('./translations/catalog-react.ru'),
  },
});

const scaffolderTranslations = createTranslationResource({
  ref: scaffolderTranslationRef,
  translations: {
    ru: () => import('./translations/scaffolder.ru'),
  },
});

const scaffolderReactTranslations = createTranslationResource({
  ref: scaffolderReactTranslationRef,
  translations: {
    ru: () => import('./translations/scaffolder-react.ru'),
  },
});

const notificationsTranslations = createTranslationResource({
  ref: notificationsTranslationRef,
  translations: {
    ru: () => import('./translations/plugin.notifications.ru'),
  },
});

const orgTranslations = createTranslationResource({
  ref: orgTranslationRef,
  translations: {
    ru: () => import('./translations/org.ru'),
  },
});

const rbacTranslations = createTranslationResource({
  ref: rbacTranslationRef,
  translations: {
    ru: () => import('./translations/rbac.ru'),
  },
});

const gitflicPipelinesTranslations = createTranslationResource({
  ref: gitflicPipelinesTranslationRef,
  translations: {
    ru: () => import('./translations/plugin-gitflic-pipelines.ru'),
  },
});

const argoCdTranslations = createTranslationResource({
  ref: argoCdTranslationRef,
  translations: {
    ru: () => import('./translations/plugin-argo-cd.ru'),
  },
});

const doraMetricsTranslations = createTranslationResource({
  ref: doraMetricsTranslationRef,
  translations: {
    ru: () => import('./translations/dora-metrics.ru'),
  },
});

const accessTranslations = createTranslationResource({
  ref: accessTranslationRef,
  translations: {
    ru: () => import('./translations/plugin-access.ru'),
  },
});
export const translations = [
  apiDocsTranslations,
  userSettingsTranslations,
  catalogTranslations,
  catalogImportTranslations,
  searchTranslations,
  searchReactTranslations,
  kubernetesClusterTranslations,
  kubernetesReactTranslations,
  kubernetesTranslations,
  techdocsTranslations,
  coreComponentsTranslations,
  catalogGraphTranslations,
  catalogReactTranslations,
  scaffolderTranslations,
  scaffolderReactTranslations,
  notificationsTranslations,
  orgTranslations,
  rbacTranslations,
  argoCdTranslations,
  doraMetricsTranslations,
  gitflicPipelinesTranslations,
  accessTranslations,
];
